import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit2, Trash2, Save, X } from 'lucide-react';
import { Holding } from '../api/portfolio';
import { updateHolding, deleteHolding } from '../api/portfolio';
import { getCompanyName } from '../utils/companyNames';

// Currency symbols mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'CAD': 'C$',
  'AUD': 'A$',
  'CHF': 'CHF',
  'GBp': 'p', // British pence
};

function getCurrencySymbol(currency?: string): string {
  if (!currency) return '$';
  return CURRENCY_SYMBOLS[currency] || currency;
}

interface PortfolioTableProps {
  portfolio: Holding[];
}

export default function PortfolioTable({ portfolio }: PortfolioTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editShares, setEditShares] = useState<number>(0);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: deleteHolding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, shares }: { id: string; shares: number }) => updateHolding(id, shares),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      setEditingId(null);
    },
  });

  const handleEdit = (holding: Holding) => {
    setEditingId(holding.id);
    setEditShares(holding.shares);
  };

  const handleSave = (id: string) => {
    updateMutation.mutate({ id, shares: editShares });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditShares(0);
  };

  if (portfolio.length === 0) {
    return (
      <div className="card text-center py-12">
        <div className="text-gray-400 mb-4">
          <Package className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No holdings yet</h3>
        <p className="text-gray-600">Click "Add Stock" to start tracking your investments</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden p-0">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Your Holdings</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Symbol
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Shares
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avg Cost
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Price
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Value
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Gain/Loss
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {portfolio.map((holding) => (
              <tr key={holding.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span 
                      className="ticker-badge"
                      title={getCompanyName(holding.symbol)}
                    >
                      {holding.symbol}
                    </span>
                    <span className="text-xs text-gray-500 hidden md:inline">
                      {getCompanyName(holding.symbol)}
                    </span>
                    {holding.purchase_date && (
                      <span className="text-xs text-gray-400">
                        ({new Date(holding.purchase_date).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {editingId === holding.id ? (
                    <input
                      type="number"
                      value={editShares}
                      onChange={(e) => setEditShares(parseFloat(e.target.value))}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                      step="0.01"
                      min="0"
                    />
                  ) : (
                    <div className="text-sm text-gray-900">{holding.shares.toFixed(4)}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="text-sm text-gray-600">
                    {holding.purchase_price ? `$${holding.purchase_price.toFixed(2)}` : '—'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="text-sm text-gray-900">
                    {holding.currentPrice ? (
                      <>
                        {getCurrencySymbol(holding.currency)}{holding.currentPrice.toFixed(2)}
                        {holding.currency && holding.currency !== 'USD' && (
                          <span className="text-xs text-gray-400 ml-1">{holding.currency}</span>
                        )}
                      </>
                    ) : '—'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {holding.totalValue ? (
                      <>
                        {getCurrencySymbol(holding.currency)}{holding.totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        {holding.currency && holding.currency !== 'USD' && (
                          <span className="text-xs text-gray-400 ml-1">{holding.currency}</span>
                        )}
                      </>
                    ) : '—'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {holding.gainLoss !== null && holding.gainLoss !== undefined ? (
                    <div className="flex flex-col items-end">
                      <span className={`text-sm font-semibold ${holding.gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {holding.gainLoss >= 0 ? '+' : ''}${holding.gainLoss.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                      <span className={`text-xs ${holding.gainLossPercent! >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ({holding.gainLossPercent! >= 0 ? '+' : ''}{holding.gainLossPercent!.toFixed(2)}%)
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {editingId === holding.id ? (
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleSave(holding.id)}
                        className="text-green-600 hover:text-green-900"
                        disabled={updateMutation.isPending}
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(holding)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(holding.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Package({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  );
}
